// Shared phase → status bucket definitions so All Claims Dashboard and the
// per-phase dashboards (Pre/Repair/Finish) always compute identical counts.
import {
  ClipboardList, FileText, Scale, Settings, Car,
  Clock, Hammer, Brush, SprayCan, Sparkles, CheckCircle2,
  ReceiptText, FileCheck2, HandCoins, Wallet,
} from "lucide-react";
import { daysSince } from "./constants";

export const isOverdue = (c) => {
  if (!c.due_date) return false;
  const d = new Date(String(c.due_date).slice(0, 10));
  return !isNaN(d) && d < new Date(new Date().toDateString());
};
export const preValue = (c) => Number(c.spk_value || 0);
export const finValue = (c) => Number(c.invoice_amount_calc || c.invoice_amount || c.spk_value || 0);
export const outstandingVal = (c) => Number(c.outstanding || 0);
export const receivedVal = (c) => Number(c.paid_amount || c.amount_paid || 0);
export const agingDays = (phase, c) =>
  phase === "REPAIR_ORDER" ? daysSince(c.unit_in_date || c.updated_at) : daysSince(c.updated_at);

export const NO_FINANCE_ROLES = ["Foreman", "Partman", "Technician", "Production", "QC", "Warehouse", "Security"];

// [key, label, icon, tone, predicate, valueFn]
export const CARDS = {
  PRE_ORDER: [
    ["CLAIM", "Claim", ClipboardList, "blue", (c) => ["WAITING_REGISTRATION", "SPK_RECEIVED"].includes(c.status) && !c.awaiting_spk_fix, preValue],
    ["WAITING_SPK", "Waiting SPK", Clock, "amber", (c) => c.awaiting_spk_fix === true, preValue],
    ["SPK", "SPK", FileText, "purple", (c) => ["REGISTRATION_COMPLETE"].includes(c.status), preValue],
    ["SPK_BANDING", "SPK Banding", Scale, "amber", (c) => c.status === "SPK_BANDING" || c.spk_banding === true, preValue],
    ["PESAN_PARTS", "Pesan Parts", Settings, "red", (c) => c.status === "WAITING_PARTS", preValue],
    ["READY_UNIT_IN", "Ready Unit In", Car, "green", (c) => ["PARTS_COMPLETE", "BOOKING_SERVICE"].includes(c.status), preValue],
  ],
  REPAIR_ORDER: [
    ["WAITING", "Waiting", Clock, "amber", (c) => ["", "UNIT_IN", "DISASSEMBLY"].includes(c.workshop_stage || (c.status === "UNIT_IN" ? "UNIT_IN" : "")), preValue],
    ["KETOK", "Ketok", Hammer, "blue", (c) => c.workshop_stage === "BODY_REPAIR", preValue],
    ["DEMPUL", "Dempul", Brush, "purple", (c) => ["DEMPUL", "PREPARATION", "READY_FOR_PAINT"].includes(c.workshop_stage), preValue],
    ["CAT", "Cat", SprayCan, "red", (c) => c.workshop_stage === "PAINT", preValue],
    ["POLES", "Poles", Sparkles, "cyan", (c) => c.workshop_stage === "POLISH", preValue],
    ["FINISH", "Finish", CheckCircle2, "green", (c) => ["QC", "READY_TO_DELIVERY"].includes(c.workshop_stage) || c.status === "READY_TO_DELIVERY", preValue],
  ],
  FINISH_ORDER: [
    ["PENDING_BILL", "Pending Bill", ReceiptText, "amber", (c) => c.status === "UNIT_OUT", finValue],
    ["BILLING", "Billing", FileText, "blue", (c) => c.status === "BILLING", finValue],
    ["INVOICE", "Invoice", FileCheck2, "purple", (c) => c.status === "INVOICE", finValue],
    ["AR", "AR (Piutang)", HandCoins, "green", (c) => c.status === "RECEIVABLE", outstandingVal],
    ["PAID", "Paid", Wallet, "green", (c) => ["PAID", "CLOSED"].includes(c.status), finValue],
  ],
};

// Dark-automotive color coding per tone (icon chip + left accent bar + glow).
export const C = {
  blue: { ic: "text-blue-300", bg: "bg-blue-500/12", ring: "ring-blue-400/30", bar: "bg-blue-500", glow: "rgba(22,132,255,0.38)" },
  purple: { ic: "text-violet-300", bg: "bg-violet-500/12", ring: "ring-violet-400/30", bar: "bg-violet-500", glow: "rgba(139,92,246,0.38)" },
  amber: { ic: "text-amber-300", bg: "bg-amber-500/12", ring: "ring-amber-400/30", bar: "bg-amber-500", glow: "rgba(245,158,11,0.38)" },
  red: { ic: "text-rose-300", bg: "bg-rose-500/12", ring: "ring-rose-400/30", bar: "bg-rose-500", glow: "rgba(244,63,94,0.38)" },
  cyan: { ic: "text-cyan-300", bg: "bg-cyan-500/12", ring: "ring-cyan-400/30", bar: "bg-cyan-500", glow: "rgba(34,211,238,0.38)" },
  green: { ic: "text-emerald-300", bg: "bg-emerald-500/12", ring: "ring-emerald-400/30", bar: "bg-emerald-500", glow: "rgba(16,185,129,0.38)" },
};

// Small chip tone for mini status summaries.
export const CHIP = {
  blue: "border-blue-400/25 bg-blue-500/10 text-blue-200",
  purple: "border-violet-400/25 bg-violet-500/10 text-violet-200",
  amber: "border-amber-400/25 bg-amber-500/10 text-amber-200",
  red: "border-rose-400/25 bg-rose-500/10 text-rose-200",
  cyan: "border-cyan-400/25 bg-cyan-500/10 text-cyan-200",
  green: "border-emerald-400/25 bg-emerald-500/10 text-emerald-200",
};
